// @ts-nocheck
import React, { useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Shield, ArrowLeft, Scale, Lock, Eye, Trash2, RefreshCw, Download } from "lucide-react";

export default function PrivacyPolicyPage() {
    useEffect(() => {
        document.title = "Πολιτική Απορρήτου — Orion Intelligence";
        window.scrollTo(0, 0);
        return () => { document.title = "Orion Intelligence"; };
    }, []);

    const sectionStyle = {
        background: "rgba(255, 255, 255, 0.03)",
        borderColor: "rgba(255, 255, 255, 0.06)"
    };

    const headingColor = "#19e6ff";
    const goldAccent = "#c9a84c";

    return (
        <div
            className="min-h-screen text-white"
            style={{
                background: `radial-gradient(ellipse at 30% 0%, rgba(25, 230, 255, 0.05), transparent 50%),
                             radial-gradient(ellipse at 70% 100%, rgba(139, 34, 82, 0.06), transparent 50%),
                             linear-gradient(170deg, #070b14, #0a0f1e)`
            }}
        >
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">

                {/* Header */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mb-12"
                >
                    <Link
                        to="/"
                        className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors mb-8"
                    >
                        <ArrowLeft size={16} />
                        Επιστροφή στην αρχική
                    </Link>

                    <div className="flex items-center gap-4 mb-6">
                        <div
                            className="p-3 rounded-2xl"
                            style={{ background: "rgba(25, 230, 255, 0.08)", border: "1px solid rgba(25, 230, 255, 0.15)" }}
                        >
                            <Shield size={28} style={{ color: headingColor }} />
                        </div>
                        <div>
                            <h1 className="text-3xl md:text-4xl font-black tracking-tight">
                                Πολιτική Απορρήτου
                            </h1>
                            <p className="text-sm text-slate-400 mt-1 uppercase tracking-widest font-medium">
                                Orion Intelligence
                            </p>
                        </div>
                    </div>

                    <div className="h-px w-full bg-gradient-to-r from-cyan-500/30 via-transparent to-transparent" />
                </motion.div>

                {/* Content */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="space-y-8"
                >
                    {/* Section 1 */}
                    <section className="rounded-2xl border p-6 md:p-8" style={sectionStyle}>
                        <h2 className="text-lg font-bold mb-4 flex items-center gap-2" style={{ color: headingColor }}>
                            <span className="text-xs font-mono opacity-50 mr-1">01</span>
                            Εισαγωγή και Ταυτότητα Υπευθύνου Επεξεργασίας
                        </h2>
                        <p className="text-slate-300 leading-relaxed mb-4">
                            Η παρούσα Πολιτική Απορρήτου καθορίζει το πλαίσιο εντός του οποίου η ατομική επιχείρηση{" "}
                            <strong className="text-white">ΔΕΛΛΙΟΣ ΔΗΜΗΤΡΙΟΣ ΤΟΥ ΑΘΑΝΑΣΙΟΥ</strong> (δ.τ.{" "}
                            <strong className="text-white">Orion Intelligence</strong>), η οποία εδρεύει στον Πολύγυρο Χαλκιδικής,
                            Ελλάδα, με ΑΦΜ 135602991 (ΔΟΥ Πολυγύρου) και αριθμό Γ.Ε.ΜΗ. 192625657000,
                            συλλέγει, επεξεργάζεται και προστατεύει τα προσωπικά δεδομένα των χρηστών
                            των ψηφιακών της πλατφόρμων.
                        </p>
                        <p className="text-slate-300 leading-relaxed">
                            Ως Υπεύθυνος Επεξεργασίας, ο Δέλλιος Δημήτριος (Orion Intelligence) δεσμεύεται για την απόλυτη προστασία
                            της ιδιωτικότητάς σας, τηρώντας αυστηρά τις διατάξεις του{" "}
                            <strong className="text-white">Γενικού Κανονισμού Προστασίας Δεδομένων (GDPR – EU 2016/679)</strong>{" "}
                            και την ισχύουσα ελληνική νομοθεσία. Η επιχείρησή μας εφαρμόζει την αρχή της ελαχιστοποίησης
                            των δεδομένων, διασφαλίζοντας ότι συλλέγονται μόνο τα απολύτως απαραίτητα στοιχεία
                            για την επίτευξη των επιδιωκόμενων σκοπών.
                        </p>
                    </section>

                    {/* Section 2 */}
                    <section className="rounded-2xl border p-6 md:p-8" style={sectionStyle}>
                        <h2 className="text-lg font-bold mb-4 flex items-center gap-2" style={{ color: headingColor }}>
                            <span className="text-xs font-mono opacity-50 mr-1">02</span>
                            Συλλογή Δεδομένων και Κατηγορίες Προσωπικών Στοιχείων
                        </h2>
                        <p className="text-slate-300 leading-relaxed mb-4">
                            Η συλλογή των δεδομένων περιορίζεται στα στοιχεία που παρέχουν οι ίδιοι οι χρήστες
                            μέσω των προϊόντων <em className="text-white">CaseMaster</em> και <em className="text-white">Lawyer Anywhere</em>.
                            Συγκεκριμένα:
                        </p>

                        <div className="space-y-4">
                            <div className="rounded-xl border border-white/5 p-5" style={{ background: "rgba(25, 230, 255, 0.02)" }}>
                                <h4 className="text-sm font-bold text-white mb-2 flex items-center gap-2">
                                    <Scale size={16} style={{ color: goldAccent }} />
                                    Waitlist (CaseMaster)
                                </h4>
                                <p className="text-slate-400 text-sm leading-relaxed">
                                    Για την εκδήλωση ενδιαφέροντος σχετικά με την πλατφόρμα διαχείρισης δικηγορικών γραφείων
                                    (<em>In Development</em>), συλλέγονται βασικά στοιχεία επικοινωνίας:{" "}
                                    <strong className="text-slate-200">Ονοματεπώνυμο</strong> και{" "}
                                    <strong className="text-slate-200">Email</strong>.
                                </p>
                            </div>

                            <div className="rounded-xl border border-white/5 p-5" style={{ background: "rgba(25, 230, 255, 0.02)" }}>
                                <h4 className="text-sm font-bold text-white mb-2 flex items-center gap-2">
                                    <Scale size={16} style={{ color: goldAccent }} />
                                    Early Access (Lawyer Anywhere)
                                </h4>
                                <p className="text-slate-400 text-sm leading-relaxed mb-3">
                                    Για την εγγραφή στην υπηρεσία πρώιμης πρόσβασης, η οποία αφορά τη σύνδεση πολιτών
                                    με δικηγόρους σε όλη την επικράτεια της Ελλάδας, συλλέγονται:
                                </p>
                                <ul className="text-sm text-slate-400 space-y-2 pl-5 list-disc marker:text-cyan-800">
                                    <li>Ονοματεπώνυμο</li>
                                    <li>Διεύθυνση ηλεκτρονικού ταχυδρομείου (Email)</li>
                                    <li>
                                        <strong className="text-slate-200">Επαγγελματική ιδιότητα:</strong> Η συλλογή
                                        του εν λόγω στοιχείου είναι απαραίτητη για την επαλήθευση της ιδιότητας
                                        των χρηστών που δικαιούνται τη δωρεάν συνδρομή ενός (1) έτους
                                        (προνόμιο για τους πρώτους 100 δικηγόρους).
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </section>

                    {/* Section 3 */}
                    <section className="rounded-2xl border p-6 md:p-8" style={sectionStyle}>
                        <h2 className="text-lg font-bold mb-4 flex items-center gap-2" style={{ color: headingColor }}>
                            <span className="text-xs font-mono opacity-50 mr-1">03</span>
                            Σκοπός και Νομική Βάση Επεξεργασίας
                        </h2>
                        <p className="text-slate-300 leading-relaxed mb-4">
                            Η επεξεργασία των δεδομένων σας διενεργείται βάσει των ακόλουθων νομικών βάσεων του GDPR:
                        </p>

                        <div className="space-y-3">
                            <div className="flex gap-4 items-start p-4 rounded-xl border border-white/5" style={{ background: "rgba(255,255,255,0.02)" }}>
                                <span className="text-xs font-bold px-2 py-1 rounded-lg shrink-0 mt-0.5"
                                    style={{ background: "rgba(25, 230, 255, 0.1)", color: headingColor }}>
                                    6(1)(α)
                                </span>
                                <div>
                                    <h4 className="text-sm font-bold text-white mb-1">Συγκατάθεση</h4>
                                    <p className="text-sm text-slate-400 leading-relaxed">
                                        Για την ένταξη στη λίστα αναμονής (Waitlist) και την αποστολή ενημερώσεων
                                        σχετικά με τη διαθεσιμότητα των προϊόντων. Η συγκατάθεση παρέχεται ρητά
                                        από τον χρήστη μέσω της υποβολής της σχετικής φόρμας.
                                    </p>
                                </div>
                            </div>
                            <div className="flex gap-4 items-start p-4 rounded-xl border border-white/5" style={{ background: "rgba(255,255,255,0.02)" }}>
                                <span className="text-xs font-bold px-2 py-1 rounded-lg shrink-0 mt-0.5"
                                    style={{ background: "rgba(25, 230, 255, 0.1)", color: headingColor }}>
                                    6(1)(β)
                                </span>
                                <div>
                                    <h4 className="text-sm font-bold text-white mb-1">Προετοιμασία Σύναψης Σύμβασης</h4>
                                    <p className="text-sm text-slate-400 leading-relaxed">
                                        Για τη διαχείριση της πρώιμης πρόσβασης (Early Access) και τη διασφάλιση
                                        των όρων παροχής της δωρεάν ετήσιας συνδρομής στους δικαιούχους.
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="mt-5 p-4 rounded-xl border-l-4" style={{ borderColor: goldAccent, background: "rgba(201, 168, 76, 0.05)" }}>
                            <p className="text-sm text-slate-300 leading-relaxed">
                                <strong className="text-white">Σημαντική Σημείωση:</strong> Έχετε το δικαίωμα να ανακαλέσετε
                                τη συγκατάθεσή σας ανά πάσα στιγμή, χωρίς να θίγεται η νομιμότητα της επεξεργασίας
                                που βασίστηκε στη συγκατάθεση προ της ανάκλησής της.
                            </p>
                        </div>
                    </section>

                    {/* Section 4 — Providers Table */}
                    <section className="rounded-2xl border p-6 md:p-8" style={sectionStyle}>
                        <h2 className="text-lg font-bold mb-4 flex items-center gap-2" style={{ color: headingColor }}>
                            <span className="text-xs font-mono opacity-50 mr-1">04</span>
                            Τρίτοι Πάροχοι Υπηρεσιών και Διαβίβαση Δεδομένων
                        </h2>
                        <p className="text-slate-300 leading-relaxed mb-6">
                            Η Orion Intelligence συνεργάζεται με κορυφαίους τεχνολογικούς παρόχους για τη διασφάλιση
                            της λειτουργικότητας και της ασφάλειας των υπηρεσιών της. Τα δεδομένα διαβιβάζονται
                            μόνο στους κάτωθι παρόχους, οι οποίοι συμμορφώνονται πλήρως με τα διεθνή πρότυπα ασφαλείας:
                        </p>

                        <div className="overflow-x-auto rounded-xl border border-white/5">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr style={{ background: "rgba(25, 230, 255, 0.05)" }}>
                                        <th className="text-left p-4 font-bold text-white border-b border-white/5">Πάροχος</th>
                                        <th className="text-left p-4 font-bold text-white border-b border-white/5">Ρόλος & Υπηρεσία</th>
                                        <th className="text-left p-4 font-bold text-white border-b border-white/5">Ασφάλεια</th>
                                    </tr>
                                </thead>
                                <tbody className="text-slate-400">
                                    <tr className="border-b border-white/5">
                                        <td className="p-4 font-medium text-white">Google Firebase / Cloud</td>
                                        <td className="p-4">Υποδομή Backend και αποθήκευση βάσης δεδομένων</td>
                                        <td className="p-4">Κρυπτογραφημένο cloud περιβάλλον</td>
                                    </tr>
                                    <tr className="border-b border-white/5">
                                        <td className="p-4 font-medium text-white">Stripe</td>
                                        <td className="p-4">Επεξεργασία πληρωμών και οικονομικών συναλλαγών</td>
                                        <td className="p-4">PCI-DSS compliance — δεν αποθηκεύουμε στοιχεία καρτών</td>
                                    </tr>
                                    <tr className="border-b border-white/5">
                                        <td className="p-4 font-medium text-white">Vercel</td>
                                        <td className="p-4">Φιλοξενία (Hosting) ιστοσελίδας & εφαρμογών</td>
                                        <td className="p-4">Ασφαλές περιβάλλον φιλοξενίας</td>
                                    </tr>
                                    <tr>
                                        <td className="p-4 font-medium text-white">Google Analytics 4</td>
                                        <td className="p-4">Συγκεντρωτικά στατιστικά επισκεψιμότητας, μόνο μετά από συγκατάθεση</td>
                                        <td className="p-4">Χωρίς Google Signals ή διαφημιστική εξατομίκευση</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </section>

                    {/* Section 5 — Analytics consent */}
                    <section className="rounded-2xl border p-6 md:p-8" style={sectionStyle}>
                        <h2 className="text-lg font-bold mb-4 flex items-center gap-2" style={{ color: headingColor }}>
                            <span className="text-xs font-mono opacity-50 mr-1">05</span>
                            Cookies και Προαιρετικά Analytics
                        </h2>
                        <div className="space-y-4 text-slate-300 leading-relaxed">
                            <p>
                                Η ιστοσελίδα δεν φορτώνει το Google Analytics πριν από τη ρητή επιλογή σας.
                                Αν επιλέξετε «Αποδοχή analytics», χρησιμοποιούμε το Google Analytics 4 με
                                αναγνωριστικό μέτρησης <strong className="text-white">G-5QQNGFF60X</strong> για
                                συγκεντρωτικά στοιχεία όπως επισκέψεις, προβολές σελίδων, συσκευή και πηγή
                                επισκεψιμότητας. Νομική βάση είναι η συγκατάθεση κατά το άρθρο 6(1)(α) GDPR.
                            </p>
                            <p>
                                Τα Google Signals, η διαφημιστική εξατομίκευση και η αποθήκευση δεδομένων
                                διαφημίσεων παραμένουν απενεργοποιημένα. Η άρνηση δεν περιορίζει τη βασική
                                λειτουργικότητα της ιστοσελίδας.
                            </p>
                            <p>
                                Μπορείτε να αλλάξετε ή να ανακαλέσετε την επιλογή σας οποτεδήποτε από το
                                κουμπί «Προτιμήσεις cookies». Με την ανάκληση διακόπτεται η μέτρηση και
                                διαγράφονται, όπου είναι τεχνικά εφικτό, τα cookies analytics της ιστοσελίδας. {" "}
                                <a
                                    href="https://policies.google.com/privacy"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="font-medium hover:underline"
                                    style={{ color: headingColor }}
                                >
                                    Πολιτική Απορρήτου της Google
                                </a>
                            </p>
                        </div>
                    </section>

                    {/* Section 6 — Rights */}
                    <section className="rounded-2xl border p-6 md:p-8" style={sectionStyle}>
                        <h2 className="text-lg font-bold mb-4 flex items-center gap-2" style={{ color: headingColor }}>
                            <span className="text-xs font-mono opacity-50 mr-1">06</span>
                            Δικαιώματα των Υποκειμένων των Δεδομένων
                        </h2>
                        <p className="text-slate-300 leading-relaxed mb-5">
                            Ως υποκείμενο των δεδομένων, διατηρείτε τα εξής δικαιώματα βάσει του GDPR:
                        </p>

                        <div className="grid gap-3 sm:grid-cols-2">
                            {[
                                { icon: Eye, title: "Δικαίωμα Πρόσβασης", desc: "Ενημέρωση για το ποια δεδομένα σας επεξεργαζόμαστε." },
                                { icon: RefreshCw, title: "Δικαίωμα Διόρθωσης", desc: "Διόρθωση ανακριβών ή συμπλήρωση ελλιπών στοιχείων." },
                                { icon: Trash2, title: "Δικαίωμα Διαγραφής", desc: "«Δικαίωμα στη Λήθη» — αίτημα διαγραφής υπό προϋποθέσεις." },
                                { icon: Lock, title: "Δικαίωμα Περιορισμού", desc: "Αναστολή επεξεργασίας σε συγκεκριμένες περιπτώσεις." },
                                { icon: Download, title: "Δικαίωμα Φορητότητας", desc: "Λήψη δεδομένων σε δομημένο μορφότυπο ή διαβίβαση." },
                                { icon: Shield, title: "Ανάκληση Συγκατάθεσης", desc: "Απόσυρση συναίνεσης για μελλοντική επεξεργασία." },
                                { icon: Scale, title: "Δικαίωμα Εναντίωσης", desc: "Αντίρρηση, ειδικά για σκοπούς εμπορικής προώθησης." }
                            ].map((right, i) => (
                                <div key={i} className="flex items-start gap-3 p-4 rounded-xl border border-white/5" style={{ background: "rgba(255,255,255,0.02)" }}>
                                    <div className="p-2 rounded-lg shrink-0" style={{ background: "rgba(25, 230, 255, 0.06)" }}>
                                        <right.icon size={16} style={{ color: headingColor }} />
                                    </div>
                                    <div>
                                        <h4 className="text-sm font-bold text-white">{right.title}</h4>
                                        <p className="text-xs text-slate-400 mt-0.5 leading-relaxed">{right.desc}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </section>

                    {/* Section 7 */}
                    <section className="rounded-2xl border p-6 md:p-8" style={sectionStyle}>
                        <h2 className="text-lg font-bold mb-4 flex items-center gap-2" style={{ color: headingColor }}>
                            <span className="text-xs font-mono opacity-50 mr-1">07</span>
                            Ασφάλεια και Διατήρηση Δεδομένων
                        </h2>
                        <p className="text-slate-300 leading-relaxed mb-4">
                            Η Orion Intelligence υιοθετεί αυστηρά τεχνικά και οργανωτικά μέτρα για την προστασία
                            της ακεραιότητας και της εμπιστευτικότητας των πληροφοριών σας:
                        </p>
                        <ul className="space-y-3 text-sm text-slate-400">
                            <li className="flex items-start gap-3">
                                <span className="w-1.5 h-1.5 rounded-full mt-2 shrink-0" style={{ background: headingColor }} />
                                <span>
                                    <strong className="text-white">Τεχνική Υποδομή:</strong> Χρησιμοποιούνται σύγχρονα frameworks
                                    ανάπτυξης (React) που ελαχιστοποιούν τις ευπάθειες διεπαφής, ενώ η αποθήκευση γίνεται σε
                                    ασφαλείς cloud υποδομές (Google Firebase) με προηγμένα πρωτόκολλα κρυπτογράφησης.
                                </span>
                            </li>
                            <li className="flex items-start gap-3">
                                <span className="w-1.5 h-1.5 rounded-full mt-2 shrink-0" style={{ background: headingColor }} />
                                <span>
                                    <strong className="text-white">Χρόνος Διατήρησης:</strong> Τα δεδομένα διατηρούνται αποκλειστικά
                                    για το χρονικό διάστημα που απαιτείται για την εκπλήρωση των σκοπών της λίστας αναμονής ή
                                    της πρώιμης πρόσβασης, εκτός εάν υφίσταται έννομη υποχρέωση για περαιτέρω διατήρηση.
                                </span>
                            </li>
                        </ul>
                    </section>

                    {/* Section 8 — Contact */}
                    <section className="rounded-2xl border p-6 md:p-8" style={sectionStyle}>
                        <h2 className="text-lg font-bold mb-4 flex items-center gap-2" style={{ color: headingColor }}>
                            <span className="text-xs font-mono opacity-50 mr-1">08</span>
                            Επικοινωνία και Υποστήριξη
                        </h2>
                        <p className="text-slate-300 leading-relaxed mb-4">
                            Για την άσκηση των δικαιωμάτων σας ή για οποιαδήποτε διευκρίνιση σχετικά με
                            την επεξεργασία των προσωπικών σας δεδομένων, μπορείτε να επικοινωνείτε
                            απευθείας με το τμήμα υποστήριξης:
                        </p>
                        <div className="inline-flex items-center gap-3 px-5 py-3 rounded-xl border border-white/10" style={{ background: "rgba(25, 230, 255, 0.04)" }}>
                            <span className="text-sm font-bold text-white">Email:</span>
                            <a href="mailto:support@orionintelligence.net" className="text-sm font-medium hover:underline" style={{ color: headingColor }}>
                                support@orionintelligence.net
                            </a>
                        </div>
                    </section>

                    {/* Section 9 */}
                    <section className="rounded-2xl border p-6 md:p-8" style={sectionStyle}>
                        <h2 className="text-lg font-bold mb-4 flex items-center gap-2" style={{ color: headingColor }}>
                            <span className="text-xs font-mono opacity-50 mr-1">09</span>
                            Τελικές Διατάξεις
                        </h2>
                        <p className="text-slate-300 leading-relaxed">
                            Η παρούσα Πολιτική Απορρήτου ενδέχεται να επικαιροποιείται καθώς τα προϊόντα μας
                            (<em>CaseMaster</em>, <em>Lawyer Anywhere</em>) εξελίσσονται από το στάδιο
                            "In Development" ή "Launching Soon" σε πλήρη κυκλοφορία. Κάθε τροποποίηση θα
                            αναρτάται στον παρόντα ιστότοπο.
                        </p>
                    </section>
                </motion.div>

                {/* Footer */}
                <div className="mt-16 pt-8 border-t border-white/5 flex flex-col sm:flex-row items-center justify-between gap-4">
                    <p className="text-xs text-slate-500">
                        © {new Date().getFullYear()} Orion Intelligence. All rights reserved.
                    </p>
                    <div className="flex gap-6 text-xs text-slate-500">
                        <Link to="/terms" className="hover:text-white transition-colors">Όροι Χρήσης</Link>
                        <Link to="/" className="hover:text-white transition-colors">Αρχική</Link>
                    </div>
                </div>
            </div>
        </div>
    );
}
